/* eslint-disable react/prop-types */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { ColumnVisibilityState, DataTableColumn } from 'types/table.types';

export interface ColumnSelectorProps<T> {
  columns: DataTableColumn<T>[];
  visibility: ColumnVisibilityState;
  onVisibilityChange: (visibility: ColumnVisibilityState) => void;
  onColumnToggle: (columnId: string) => void;
}

export const ColumnSelector = React.memo(
  React.forwardRef<HTMLDivElement, ColumnSelectorProps<any>>(
    ({ columns, visibility, onVisibilityChange, onColumnToggle }, ref) => {
      const [isOpen, setIsOpen] = useState(false);

      const hiddenColumnCount = useMemo(() => {
        return Object.values(visibility).filter((v) => !v).length;
      }, [visibility]);

      const handleSelectAll = useCallback(() => {
        const allVisible = columns.reduce((acc, col) => {
          const key = (col.accessorKey as string) || (col.id as string) || '';
          acc[key] = true;
          return acc;
        }, {} as ColumnVisibilityState);
        onVisibilityChange(allVisible);
      }, [columns, onVisibilityChange]);

      const handleDeselectAll = useCallback(() => {
        const allHidden = columns.reduce((acc, col) => {
          const key = (col.accessorKey as string) || (col.id as string) || '';
          acc[key] = false;
          return acc;
        }, {} as ColumnVisibilityState);
        onVisibilityChange(allHidden);
      }, [columns, onVisibilityChange]);

      return (
        <div ref={ref} className="column-selector">
          <Dropdown isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
            <DropdownToggle color="info" outline caret className="">
              <i className="ri-layout-column-line"></i>
              Columns
              {hiddenColumnCount > 0 && (
                <Badge color="secondary" className="ms-2">
                  {hiddenColumnCount}
                </Badge>
              )}
            </DropdownToggle>

            <DropdownMenu end className="p-3" style={{ minWidth: '250px' }}>
              {/* Select/Deselect All */}
              <div className="mb-3 pb-2 border-bottom">
                <FormGroup>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary flex-grow-1"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger flex-grow-1"
                      onClick={handleDeselectAll}
                    >
                      Deselect All
                    </button>
                  </div>
                </FormGroup>
              </div>

              {/* Column Checkboxes */}
              {columns.map((col) => {
                const key = (col.accessorKey as string) || (col.id as string) || '';
                const label = typeof col.header === 'string' ? col.header : `Column ${key}`;
                const isVisible = visibility[key] !== false;

                return (
                  <FormGroup key={key} check className="mb-2">
                    <Input
                      id={`col-${key}`}
                      type="checkbox"
                      checked={isVisible}
                      onChange={() => onColumnToggle(key)}
                    />
                    <Label htmlFor={`col-${key}`} check className="mb-0 ms-2">
                      <small>{label}</small>
                    </Label>
                  </FormGroup>
                );
              })}
            </DropdownMenu>
          </Dropdown>
        </div>
      );
    },
  ),
);

ColumnSelector.displayName = 'ColumnSelector';
